import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  Button,
  IconButton,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  // Badge,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Stack,
  alpha,
  Card,
  CardContent,
  Collapse,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  // CheckCircle as CheckCircleIcon,
  // Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  // Subscriptions as SubscriptionIcon,
  AdminPanelSettings as AdminIcon,
  School as StudentIcon,
  Business as BusinessIcon,
  Psychology as TeacherIcon,
  Computer as IITIcon,
  Print as PrintIcon,
  // Download as DownloadIcon,
  People as PeopleIcon,
  // MoreVert as MoreVertIcon,
  // MarkEmailRead as MarkEmailReadIcon,
  // Smartphone as SmartphoneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  // Visibility as VisibilityIcon,
  // FilterList as FilterIcon,
  // Search as SearchIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AuthService from '../Auth/AuthService';

// Styled components
const RootContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(4),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  minHeight: '100vh'
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 20,
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)'
  }
}));

const StyledAvatar = styled(Avatar)(({ theme, role }) => ({
  width: 40,
  height: 40,
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: role === 'ADMIN' ? theme.palette.error.main :
    role === 'STUDENT' ? theme.palette.primary.main :
      role === 'TEACHER' ? '#dadedd' :
        role === 'BUSINESS' ? '#fdcb6e' :
          role === 'IITIAN' ? '#6c5ce7' :
            theme.palette.grey[500],
  fontSize: 16,
  fontWeight: 700,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.7rem',
  padding: '0 6px',
  height: 24,
  borderRadius: 6,
  ...(status === true && {
    backgroundColor: '#e4e8e7',
    // color: 'white',
  }),
  ...(status === false && {
    backgroundColor: '#ff7675',
    // color: 'white',
  }),
  ...(status === null && {
    backgroundColor: '#fdcb6e',
    // color: 'white',
  })
}));

const RoleChip = styled(Chip)(({ theme, role }) => ({
  fontWeight: 600,
  fontSize: '0.65rem',
  height: 22,
  backgroundColor: role === 'ADMIN' ? alpha(theme.palette.error.main, 0.1) :
    role === 'STUDENT' ? alpha(theme.palette.primary.main, 0.1) :
      role === 'TEACHER' ? alpha('#cfd1d1', 0.1) :
        role === 'BUSINESS' ? alpha('#fdcb6e', 0.1) :
          role === 'IITIAN' ? alpha('#6c5ce7', 0.1) :
            alpha(theme.palette.grey[500], 0.1),
  color: role === 'ADMIN' ? theme.palette.error.main :
    role === 'STUDENT' ? theme.palette.primary.main :
      role === 'TEACHER' ? '#014336' :
        role === 'BUSINESS' ? '#fdcb6e' :
          role === 'IITIAN' ? '#6c5ce7' :
            theme.palette.grey[500],
}));

// const StatsCard = styled(Card)(({ theme }) => ({
//   padding: theme.spacing(2),
//   borderRadius: 16,
//   background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
//   border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//   transition: 'all 0.3s ease',
//   '&:hover': {
//     transform: 'translateY(-4px)',
//     boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
//   }
// }));

const MobileCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
  }
}));

// Main Component
const EmployeeLoginDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loginData, setLoginData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('asc');
  const [expandedMobile, setExpandedMobile] = useState(null);

  const loadLoginDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AuthService.getAuth(
        AuthService.pathUrl("GET", "/auth/emp-login")
      );

      const userData = Array.isArray(data) ? data : [data];
      setLoginData(userData);
    } catch (err) {
      setError(err.message || 'Failed to load login details');
      setSnackbar({
        open: true,
        message: 'Failed to load login details. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadLoginDetails();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadLoginDetails();
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditingData({ ...user });
    setEditDialog(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setDeleteDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await AuthService.putAuth(
        AuthService.pathUrl("PUT", "/auth/"),
        editingData.id, editingData
      );

      const updatedList = loginData.map(user =>
        user.id === editingData.id ? editingData : user
      );
      setLoginData(updatedList);
      setEditDialog(false);
      setSnackbar({
        open: true,
        message: 'User details updated successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to update user details. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await AuthService.deleteAuth(
        AuthService.pathUrl("DELETE", `/auth/${selectedUser.id}`)
      );

      const updatedList = loginData.filter(user => user.id !== selectedUser.id);
      setLoginData(updatedList);
      setDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'User deleted successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete user. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = !user.isActive;
      await AuthService.putAuth(
        AuthService.pathUrl("PUT", "/auth/" + user.id),
        { ...user, isActive: newStatus }
      );

      const updatedList = loginData.map(u =>
        u.id === user.id ? { ...u, isActive: newStatus } : u
      );
      setLoginData(updatedList);

      setSnackbar({
        open: true,
        message: `User ${newStatus ? 'activated' : 'deactivated'} successfully!`,
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to toggle user status. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getRoleIcon = (role) => {
    switch (role?.toUpperCase()) {
      case 'ADMIN':
        return <AdminIcon sx={{ fontSize: 16 }} />;
      case 'STUDENT':
        return <StudentIcon sx={{ fontSize: 16 }} />;
      case 'TEACHER':
        return <TeacherIcon sx={{ fontSize: 16 }} />;
      case 'BUSINESS':
        return <BusinessIcon sx={{ fontSize: 16 }} />;
      case 'IITIAN':
        return <IITIcon sx={{ fontSize: 16 }} />;
      default:
        return <PersonIcon sx={{ fontSize: 16 }} />;
    }
  };

  // const formatDate = (dateString) => {
  //   if (!dateString) return 'N/A';
  //   try {
  //     return new Date(dateString).toLocaleDateString('en-IN', {
  //       day: '2-digit',
  //       month: 'short',
  //       year: 'numeric'
  //     });
  //   } catch {
  //     return 'Invalid Date';
  //   }
  // };

  const getStatusLabel = (value) => {
    if (value === true) return 'Verified';
    if (value === false) return 'Not Verified';
    return 'Pending';
  };

  // Sort data
  const sortedData = [...loginData].sort((a, b) => {
    const aValue = a[orderBy] || '';
    const bValue = b[orderBy] || '';
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const totalUsers = loginData.length;
  // const activeUsers = loginData.filter(u => u.isActive).length;
  // const verifiedEmails = loginData.filter(u => u.isEmailVerify).length;
  // const verifiedMobiles = loginData.filter(u => u.isMobileVerify).length;

  if (loading) {
    return (
      <RootContainer maxWidth="xl">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Skeleton variant="text" width={300} height={40} />
          <Box display="flex" gap={2}>
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
        <Grid container spacing={3} mb={4}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
      </RootContainer>
    );
  }

  if (error && !loading) {
    return (
      <RootContainer maxWidth="xl">
        <GlassPaper sx={{ textAlign: 'center', py: 8 }}>
          <ErrorIcon sx={{ fontSize: 72, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom fontWeight="600">
            Error Loading User Details
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Retry
          </Button>
        </GlassPaper>
      </RootContainer>
    );
  }

  if (!loginData || loginData.length === 0) {
    return (
      <RootContainer maxWidth="xl">
        <GlassPaper sx={{ textAlign: 'center', py: 8 }}>
          <PeopleIcon sx={{ fontSize: 72, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight="600">
            No Users Found
          </Typography>
          <Typography variant="body1" color="textSecondary">
            No employee login details available.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            Refresh
          </Button>
        </GlassPaper>
      </RootContainer>
    );
  }

  return (
    <RootContainer maxWidth="xl">
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          {/* <IconButton
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <ArrowBackIcon />
          </IconButton> */}
          <Box>
            <Typography variant="h5" fontWeight="700" color="primary">
              Employee Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <PeopleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              {totalUsers} user{totalUsers > 1 ? 's' : ''} found
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Print
          </Button>
          {/* <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button> */}
        </Box>
      </Box>

      {/* Stats Cards */}
      {/* <Grid container spacing={3} mb={4}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 40, height: 40 }}>
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="700">{totalUsers}</Typography>
                <Typography variant="caption" color="textSecondary">Total Users</Typography>
              </Box>
            </Box>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: alpha('#00b894', 0.1), color: '#00b894', width: 40, height: 40 }}>
                <CheckCircleIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="700">{activeUsers}</Typography>
                <Typography variant="caption" color="textSecondary">Active</Typography>
              </Box>
            </Box>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: alpha('#6c5ce7', 0.1), color: '#6c5ce7', width: 40, height: 40 }}>
                <MarkEmailReadIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="700">{verifiedEmails}</Typography>
                <Typography variant="caption" color="textSecondary">Email Verified</Typography>
              </Box>
            </Box>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: alpha('#fdcb6e', 0.1), color: '#fdcb6e', width: 40, height: 40 }}>
                <SmartphoneIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="700">{verifiedMobiles}</Typography>
                <Typography variant="caption" color="textSecondary">Mobile Verified</Typography>
              </Box>
            </Box>
          </StatsCard>
        </Grid>
      </Grid> */}

      {/* Table View */}
      <GlassPaper>
        {isMobile ? (
          // Mobile Card View
          <Box>
            <Typography variant="h6" fontWeight="700" gutterBottom>
              Users
              <Chip
                label={totalUsers}
                size="small"
                color="primary"
                sx={{ ml: 1, fontWeight: 600, borderRadius: 2 }}
              />
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {paginatedData.map((user) => (
              <MobileCard key={user.id}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <StyledAvatar role={user.roleType}>
                      {user.email?.charAt(0).toUpperCase() || 'U'}
                    </StyledAvatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle1" fontWeight="600" noWrap>
                        {user.email?.split('@')[0] || 'User'}
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <RoleChip
                          role={user.roleType}
                          label={user.roleType}
                          size="small"
                          icon={getRoleIcon(user.roleType)}
                        />
                        <StatusChip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          status={user.isActive}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedMobile(expandedMobile === user.id ? null : user.id)}
                    >
                      {expandedMobile === user.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedMobile === user.id}>
                    <Box mt={2} pt={2} borderTop={`1px solid ${alpha(theme.palette.divider, 0.5)}`}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="textSecondary">Email</Typography>
                            <Typography variant="body2" noWrap>{user.email}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="textSecondary">Mobile</Typography>
                            <Typography variant="body2">{user.mobile}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="textSecondary">Login Type</Typography>
                            <Typography variant="body2">{user.loginType || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="textSecondary">Status</Typography>
                            <Box display="flex" gap={0.5}>
                              <StatusChip
                                label={getStatusLabel(user.isEmailVerify)}
                                status={user.isEmailVerify}
                                size="small"
                              />
                              <StatusChip
                                label={getStatusLabel(user.isMobileVerify)}
                                status={user.isMobileVerify}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Switch
                              checked={user.isActive}
                              onChange={() => handleToggleStatus(user)}
                              size="small"
                              color="success"
                            />
                            <Box>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(user)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(user)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </CardContent>
              </MobileCard>
            ))}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={loginData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        ) : (
          // Desktop Table View
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="700">
                Users
                <Chip
                  label={totalUsers}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, fontWeight: 600, borderRadius: 2 }}
                />
              </Typography>
              {/* <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Filter
              </Button> */}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'id'}
                        direction={orderBy === 'id' ? order : 'asc'}
                        onClick={() => handleSort('id')}
                      >
                        <Typography fontWeight="600">ID</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleSort('email')}
                      >
                        <Typography fontWeight="600">User</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'roleType'}
                        direction={orderBy === 'roleType' ? order : 'asc'}
                        onClick={() => handleSort('roleType')}
                      >
                        <Typography fontWeight="600">Role</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'loginType'}
                        direction={orderBy === 'loginType' ? order : 'asc'}
                        onClick={() => handleSort('loginType')}
                      >
                        <Typography fontWeight="600">Login Type</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="600">Verification</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={orderBy === 'isActive'}
                        direction={orderBy === 'isActive' ? order : 'asc'}
                        onClick={() => handleSort('isActive')}
                      >
                        <Typography fontWeight="600">Status</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="600">Actions</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          #{user.id?.toString().padStart(4, '0')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <StyledAvatar role={user.roleType}>
                            {user.email?.charAt(0).toUpperCase() || 'U'}
                          </StyledAvatar>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {user.email?.split('@')[0] || 'User'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <RoleChip
                          role={user.roleType}
                          label={user.roleType}
                          size="small"
                          icon={getRoleIcon(user.roleType)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.loginType || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title={user.isEmailVerify ? 'Email Verified' : 'Email Not Verified'}>
                            <StatusChip
                              label={user.isEmailVerify ? 'Email' : 'Email'}
                              status={user.isEmailVerify}
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title={user.isMobileVerify ? 'Mobile Verified' : 'Mobile Not Verified'}>
                            <StatusChip
                              label={user.isMobileVerify ? 'Mobile' : 'Mobile'}
                              status={user.isMobileVerify}
                              size="small"
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={user.isActive}
                          onChange={() => handleToggleStatus(user)}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(user)}
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(user)}
                              sx={{
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.2) }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={loginData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </GlassPaper>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 1 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="700">
            Edit User Details
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Update user information and permissions
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                value={editingData?.email || ''}
                onChange={(e) => setEditingData({ ...editingData, email: e.target.value })}
                InputProps={{
                  startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={editingData?.mobile || ''}
                onChange={(e) => setEditingData({ ...editingData, mobile: e.target.value })}
                InputProps={{
                  startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role Type</InputLabel>
                <Select
                  value={editingData?.roleType || ''}
                  onChange={(e) => setEditingData({ ...editingData, roleType: e.target.value })}
                  label="Role Type"
                >
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                  <MenuItem value="STUDENT">Student</MenuItem>
                  <MenuItem value="TEACHER">Teacher</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Login Type</InputLabel>
                <Select
                  value={editingData?.loginType || ''}
                  onChange={(e) => setEditingData({ ...editingData, loginType: e.target.value })}
                  label="Login Type"
                >
                  {/* <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="MOBILE">Mobile</MenuItem>
                  <MenuItem value="GOOGLE">Google</MenuItem>
                  <MenuItem value="FACEBOOK">Facebook</MenuItem> */}
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                  <MenuItem value="SCHOOL">School</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={3} flexWrap="wrap" sx={{ p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.03), borderRadius: 2 }}>
                <FormControl>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={editingData?.isActive || false}
                      onChange={(e) => setEditingData({ ...editingData, isActive: e.target.checked })}
                      color="success"
                    />
                    <Typography variant="body2" fontWeight="500">Active</Typography>
                  </Box>
                </FormControl>
                <FormControl>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={editingData?.isEmailVerify || false}
                      onChange={(e) => setEditingData({ ...editingData, isEmailVerify: e.target.checked })}
                      color="success"
                    />
                    <Typography variant="body2" fontWeight="500">Email Verified</Typography>
                  </Box>
                </FormControl>
                <FormControl>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={editingData?.isMobileVerify || false}
                      onChange={(e) => setEditingData({ ...editingData, isMobileVerify: e.target.checked })}
                      color="success"
                    />
                    <Typography variant="body2" fontWeight="500">Mobile Verified</Typography>
                  </Box>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        PaperProps={{
          sx: { borderRadius: 4, p: 1, maxWidth: 400 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="700" color="error">
            Delete User
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            <DeleteIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this user?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>{selectedUser?.email}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={() => setDeleteDialog(false)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </RootContainer>
  );
};

export default EmployeeLoginDetails;